import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Link from '@base/Link/Link';
import Image from '@base/Image';
import Menu from '@icon/Menu';
import Close from '@icon/Close';
import { Fonts, mediaQuery, Spacings } from '@styles/index';

const NAV_ITEMS = [
  { id: 'scholarship-benefits', label: 'Benefits' },
  { id: 'scholarship-tests', label: 'Tests' },
  {
    id: 'scholarship-syllabuses',
    label: 'Syllabus',
    requiresSyllabus: true
  },
  { id: 'scholarship-share', label: 'Share' },
  { id: 'scholarship-faq', label: 'FAQ' }
];

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  background: ${({ $scrolled, $menuOpen }) =>
    $scrolled || $menuOpen
      ? 'rgba(8, 12, 28, 0.78)'
      : 'linear-gradient(180deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0) 100%)'};
  backdrop-filter: ${({ $scrolled, $menuOpen }) =>
    $scrolled || $menuOpen ? 'blur(18px)' : 'none'};
  box-shadow: ${({ $scrolled, $menuOpen }) =>
    $scrolled || $menuOpen ? '0 8px 24px rgba(0, 0, 0, 0.2)' : 'none'};
  transition: background 0.25s ease, box-shadow 0.25s ease,
    backdrop-filter 0.25s ease;
`;

const Inner = styled.div`
  max-width: 1136px;
  margin: 0 auto;
  min-height: ${Spacings.SPACING_HEADER}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacings.SPACING_4B};
  padding: 0 ${Spacings.SPACING_6B};
  ${mediaQuery.uptoTablet} {
    min-height: ${Spacings.SPACING_14B};
    padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
    flex-wrap: wrap;
  }
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  img {
    width: 161px;
    height: 24px;
  }
  ${mediaQuery.uptoTablet} {
    img {
      width: 128px;
      height: 19px;
    }
  }
`;

const MenuToggle = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_10B};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${Spacings.SPACING_3B};
  background: rgba(255, 255, 255, 0.08);
  color: var(--color-i-white);
  cursor: pointer;
  ${mediaQuery.uptoTablet} {
    display: inline-flex;
    margin-left: auto;
  }
`;

const NavList = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
  margin-left: auto;
  ${mediaQuery.uptoTablet} {
    display: ${({ $open }) => ($open ? 'grid' : 'none')};
    grid-template-columns: 1fr 1fr;
    width: 100%;
    padding-bottom: ${Spacings.SPACING_2B};
  }
`;

const NavItem = styled.button`
  border: 1px solid
    ${({ $active }) => ($active ? 'transparent' : 'rgba(255, 255, 255, 0.18)')};
  background: ${({ $active }) =>
    $active ? 'var(--color-i-white)' : 'rgba(255, 255, 255, 0.08)'};
  color: ${({ $active }) =>
    $active ? 'var(--color-i-black-4)' : 'var(--color-i-white)'};
  border-radius: ${Spacings.SPACING_10B};
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_5B};
  font-size: ${Fonts.SMALL};
  font-weight: ${Fonts.SEMIBOLD};
  line-height: ${Fonts.BUTTON_LINE_HEIGHT};
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease,
    border-color 0.2s ease;
  &:hover {
    background: ${({ $active }) =>
      $active ? 'var(--color-i-white)' : 'rgba(255, 255, 255, 0.18)'};
    transform: translateY(-1px);
  }
  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_3B};
    font-size: ${Fonts.ULTRA_SMALL};
    white-space: normal;
  }
`;

const TransparentNavbar = ({ showClassAndSyllabuses = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const navItems = NAV_ITEMS.filter(
    (item) => !item.requiresSyllabus || showClassAndSyllabuses
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleItemClick = (item) => {
    setActiveId(item.id);
    setMenuOpen(false);
    const section = document.getElementById(item.id);
    if (!section) return;
    const top =
      section.getBoundingClientRect().top +
      window.scrollY -
      Spacings.SPACING_HEADER;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  };

  return (
    <Nav
      $scrolled={scrolled}
      $menuOpen={menuOpen}
      aria-label="Scholarship navigation"
    >
      <Inner>
        {scrolled && (
          <Link href="/" as="/">
            <Brand>
              <Image
                priority
                src="prodigy/un_logo.svg"
                alt="Unacademy"
                width={161}
                height={24}
              />
            </Brand>
          </Link>
        )}
        <MenuToggle
          type="button"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? (
            <Close size="20px" color="var(--color-i-white)" />
          ) : (
            <Menu
              size="20px"
              viewBox="0 0 24 24"
              stroke="var(--color-i-white)"
            />
          )}
        </MenuToggle>
        <NavList $open={menuOpen}>
          {navItems.map((item) => (
            <NavItem
              key={item.id}
              type="button"
              $active={activeId === item.id}
              onClick={() => handleItemClick(item)}
            >
              {item.label}
            </NavItem>
          ))}
        </NavList>
      </Inner>
    </Nav>
  );
};

export default TransparentNavbar;
